import React, { useEffect, useState, useRef } from "react";
import { Box, Typography, Avatar, Button, Chip, Paper } from "@mui/material";
import PhoneIcon from "@mui/icons-material/Phone";
import { useCallContext } from "../context/CallContext";
import newCallSound from "../assets/new-call.mp3";

const CallNotification = () => {
  const { incomingCall, acceptCall, rejectCall, outgoingCall, endOutgoingCall } = useCallContext();

  // Estado para o tempo de duração da ligação efetuada
  const [callDuration, setCallDuration] = useState(0);

  // Referência ao objeto de áudio
  const audioRef = useRef(null);

  // Dados do chamador: vindo da conversa (socket newCall) ou fallback/mock
  const conversation = incomingCall?.conversation;
  const parameters = incomingCall?.parameters;
  const isGupshupSipCall = incomingCall?.kind === "whatsapp_gupshup_sip";
  const callDetails = {
    name: conversation?.name ?? parameters?.From ?? "Chamador",
    phoneNumber: parameters?.From ?? conversation?.phoneNumber ?? conversation?.name ?? "—",
    protocol: parameters?.Protocol ?? conversation?.id ?? conversation?.protocol ?? "—",
    channel: conversation?.channel ?? "Telefone",
    status: conversation?.status ?? "Pendente",
    assignedTo: conversation?.assignedTo ?? "—",
    icon: <PhoneIcon fontSize="small" />,
  };

  useEffect(() => {
    if (incomingCall) {        
      // Criar o áudio apenas se houver chamada recebida
      if (!audioRef.current) {
        audioRef.current = new Audio(newCallSound);
        audioRef.current.loop = true; // Configura o áudio para tocar em loop
      }

      // Tocar o som
      audioRef.current.play().catch((err) => console.error("Erro ao reproduzir áudio:", err));
    } else {
      // Parar o som e limpar o áudio quando a chamada terminar
      if (audioRef.current) {
        audioRef.current.pause();
        audioRef.current.currentTime = 0;
        audioRef.current = null;
      }
    }

    // Cleanup ao desmontar o componente
    return () => {
      if (audioRef.current) {
        audioRef.current.pause();
        audioRef.current = null;
      }
    };
  }, [incomingCall]);

  // Inicia o temporizador para ligação efetuada
  useEffect(() => {
    let interval = null;

    setCallDuration(0);
    if (outgoingCall) {
      interval = setInterval(() => {
        setCallDuration((prev) => prev + 1); // Incrementa o tempo a cada segundo
      }, 1000);
    }

    return () => clearInterval(interval); // Cleanup ao encerrar a ligação
  }, [outgoingCall]);

  const formatDuration = (seconds) => {
    const minutes = Math.floor(seconds / 60);
    const remainingSeconds = seconds % 60;
    return `${String(minutes).padStart(2, "0")}:${String(remainingSeconds).padStart(2, "0")}`;
  };

  const handleAcceptCall = async () => {
    if (audioRef.current) {
      audioRef.current.pause();
      audioRef.current.currentTime = 0;
      audioRef.current = null;
    }
    await acceptCall();
  };

  const handleRejectCall = async () => {
    if (audioRef.current) {
      audioRef.current.pause();
      audioRef.current.currentTime = 0;
      audioRef.current = null;
    }
    await rejectCall();
  };

  return (
    <Box sx={{ position: "fixed", top: 16, right: 16, display: "flex", flexDirection: "column", gap: 2, zIndex: 1400 }}>
      {/* Notificação de Chamada Recebida */}
      {incomingCall && (
        <Paper
          elevation={3}
          sx={{
            padding: 2,
            display: "flex",
            alignItems: "center",
            gap: 2,
            backgroundColor: "#333",
            color: "#fff",
            borderRadius: 2,
            minWidth: "350px",
          }}
        >
          {/* Avatar do Chamador */}
          <Avatar
            sx={{
              backgroundColor: "#00796B",
              width: 48,
              height: 48,
            }}
          >
            {callDetails.name[0]}
          </Avatar>

          {/* Informações do Chamador */}
          <Box flexGrow={1}>
            <Typography variant="subtitle1" fontWeight="bold" noWrap>
              {callDetails.name}
            </Typography>
            <Typography variant="body2" noWrap>
              Chamando pelo {callDetails.channel}
            </Typography>
            {isGupshupSipCall && (
              <Typography variant="caption" sx={{ display: "block", mt: 0.5, opacity: 0.9, maxWidth: 280 }}>
                Modo SIP (Gupshup): o áudio não passa pelo navegador. Atenda pelo telefone/SBC configurado para este canal.
              </Typography>
            )}
            <Typography variant="body2" noWrap>
              Telefone: {callDetails.phoneNumber}
            </Typography>
            <Typography variant="body2" noWrap>
              Protocolo: {callDetails.protocol}
            </Typography>
            <Box display="flex" alignItems="center" gap={1} mt={1}>
              {callDetails.icon}
              <Chip
                label={callDetails.assignedTo}
                size="small"
                sx={{
                  backgroundColor: "#E0E0E0",
                  color: "#333",
                  fontWeight: "bold",
                }}
              />
              <Chip
                label={callDetails.status}
                size="small"
                sx={{
                  backgroundColor: "#AED581",
                  color: "#2E7D32",
                  fontWeight: "bold",
                }}
              />
            </Box>
          </Box>

          {/* Botões de Ação */}
          <Box display="flex" flexDirection="column" gap={1}>
            <Button
              size="small"
              variant="contained"
              color="success"
              onClick={handleAcceptCall}
              sx={{ textTransform: "none" }}
            >
              Aceitar
            </Button>
            <Button
              size="small"
              variant="contained"
              color="error"
              onClick={handleRejectCall}
              sx={{ textTransform: "none" }}
            >
              Rejeitar
            </Button>
          </Box>
        </Paper>
      )}

      {/* Notificação de Ligação Efetuada */}
      {outgoingCall && (
        <Paper
          elevation={3}
          sx={{
            padding: 2,
            display: "flex",
            alignItems: "center",
            gap: 2,
            backgroundColor: "#333",
            color: "#fff",
            borderRadius: 2,
            minWidth: "350px",
          }}
        >
          <Avatar
            sx={{
              backgroundColor: "#00796B",
              width: 48,
              height: 48,
            }}
          >
            {callDetails.name[0]}
          </Avatar>
          <Box flexGrow={1}>
            <Typography variant="subtitle1" fontWeight="bold" noWrap>
              Ligação em andamento 
            </Typography>
            <Typography variant="body2" fontWeight="bold" color="success.main">
              Duração: {formatDuration(callDuration)}
            </Typography>
          </Box>
          <Button
              size="small"
              variant="contained"
              color="error"
              onClick={endOutgoingCall}
              sx={{ textTransform: "none" }}
            >
              Desligar
            </Button>
        </Paper>
      )}
    </Box>
  );
};

export default CallNotification;
